import React, { useMemo } from 'react';
import { Sparkles, Store, ChevronRight, Award, Percent, Tag, Zap, ShoppingBag, Cake, Ticket } from 'lucide-react';

const getDiscountMeta = (d) => {
    if (d.isReward) {
        return { icon: Award, color: '#a855f7', label: `${d.requiredPoints || 0} puan ile kazanın` };
    }
    const isBundle = d.campaignType === 'bundle' || d.campaignType === 'couponBundle';
    if (isBundle) {
        const buyQ = d.buyQuantity || 1;
        const disc = d.bundleDiscountValue || 100;
        const buyNames = (d.buyItems || []).slice(0, 2).join(', ') || 'seçili ürünler';
        const discText = disc === 100 || disc === '100' ? 'bedava' : `%${disc} indirimli`;
        return { icon: ShoppingBag, color: '#f59e0b', label: `${buyQ} al, ${discText} kazan` };
    }
    if (d.birthdayOnly) {
        return { icon: Cake, color: '#ec4899', label: 'Doğum gününüze özel' };
    }
    const isCoupon = d.campaignType === 'coupon';
    if (d.type === 'percentage') {
        return { icon: isCoupon ? Ticket : Percent, color: isCoupon ? '#3b82f6' : '#ef4444', label: `%${d.value} indirim${isCoupon ? ' kuponu' : ''}` };
    }
    if (d.type === 'fixed') {
        return { icon: isCoupon ? Ticket : Tag, color: isCoupon ? '#3b82f6' : '#22c55e', label: `${d.value} TL indirim${isCoupon ? ' kuponu' : ''}` };
    }
    if (d.type === 'multiplier') {
        return { icon: Zap, color: '#8b5cf6', label: `${d.value}x puan çarpanı` };
    }
    return { icon: Sparkles, color: 'var(--primary)', label: 'Özel İndirim Fırsatı' };
};

const GlobalExploreTab = ({ businesses, allBusinesses = [], selectedFilter = 'all', businessPrefs = {}, navigate }) => {
    const filteredBusinesses = useMemo(() => {
        const visibleMember = businesses.filter(b => !businessPrefs[b.id]?.hidden);
        if (selectedFilter === 'favorites') {
            return visibleMember.filter(b => businessPrefs[b.id]?.favorite);
        }
        return visibleMember;
    }, [businesses, selectedFilter, businessPrefs]);

    // Filter and sort discounts per business
    const withDiscounts = useMemo(() => {
        return filteredBusinesses
            .map(biz => {
                // Kuponları gizle, sıralama: ödüller > indirimler > doğum günü
                const sorted = [...(biz.discounts || [])]
                    .filter(d => d.campaignType !== 'coupon' && d.campaignType !== 'couponBundle')
                    .sort((a, b) => {
                        const order = (d) => {
                            if (d.isReward) return 0;
                            if (d.birthdayOnly) return 2;
                            return 1;
                        };
                        return order(a) - order(b);
                    });

                return { ...biz, discounts: sorted };
            })
            .filter(b => b.discounts && b.discounts.length > 0);
    }, [filteredBusinesses]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {withDiscounts.length === 0 ? (
                <div className="card" style={{ padding: '3rem', textAlign: 'center', background: 'white', borderRadius: '24px' }}>
                    <Sparkles size={48} style={{ opacity: 0.2, marginBottom: '1rem', color: 'var(--primary)' }} />
                    <p style={{ color: '#666' }}>
                        {selectedFilter === 'favorites'
                            ? 'Favori işletmelerinizde aktif kampanya bulunmuyor.'
                            : selectedFilter === 'nearby'
                                ? 'Yakındaki işletmelerde aktif kampanya bulunmuyor.'
                                : 'Şu anda aktif bir kampanya bulunmuyor.'}
                    </p>
                </div>
            ) : (
                withDiscounts.map(biz => {
                    const points = biz.customer?.points || 0;

                    return (
                        <div
                            key={biz.id}
                            style={{
                                padding: '1.25rem',
                                background: '#ffffff',
                                borderRadius: '24px',
                                boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
                                border: '1px solid rgba(0,0,0,0.03)'
                            }}
                        >
                            {/* Business Header with Points */}
                            <div
                                onClick={() => navigate(`/portal/${biz.tenant?.slug || biz.id}`)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    marginBottom: '1rem',
                                    paddingBottom: '0.75rem',
                                    borderBottom: '1px solid #f3f4f6',
                                    cursor: 'pointer'
                                }}
                            >
                                <div style={{
                                    width: '48px', height: '48px', borderRadius: '14px', background: 'var(--bg-main)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                                }}>
                                    {biz.tenant.theme?.logoUrl ? (
                                        <img src={biz.tenant.theme.logoUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Logo" />
                                    ) : <Store size={24} color="var(--primary)" />}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1a1412' }}>{biz.tenant.name}</h3>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span>İşletmeye Git</span>
                                        <ChevronRight size={14} />
                                    </div>
                                </div>
                                <div style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    background: 'rgba(var(--primary-rgb), 0.06)', padding: '8px 14px',
                                    borderRadius: '14px', minWidth: '60px'
                                }}>
                                    <span style={{ fontSize: '1.1rem', fontWeight: 900, color: 'var(--primary)' }}>{points}</span>
                                    <span style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Puan</span>
                                </div>
                            </div>

                            {/* Discounts List */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {biz.discounts.map((discount, idx) => {
                                    const meta = getDiscountMeta(discount);
                                    const Icon = meta.icon;

                                    return (
                                        <div
                                            key={`${discount.id}-${idx}`}
                                            onClick={() => navigate(`/portal/${biz.tenant?.slug || biz.id}`)}
                                            style={{
                                                background: '#f9fafb',
                                                padding: '12px',
                                                borderRadius: '16px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = `rgba(var(--primary-rgb), 0.05)`;
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = '#f9fafb';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            <div style={{
                                                width: '36px', height: '36px', borderRadius: '10px',
                                                background: `${meta.color}10`, color: meta.color,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <Icon size={18} />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1412', marginBottom: '2px' }}>{discount.title}</h4>
                                                <p style={{ fontSize: '0.8rem', color: meta.color, fontWeight: 600, lineHeight: 1.3 }}>
                                                    {meta.label}
                                                </p>
                                            </div>
                                            <div style={{
                                                background: 'white',
                                                padding: '6px 12px',
                                                borderRadius: '20px',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                color: 'var(--primary)',
                                                boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                                            }}>
                                                İncele
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );
};

export default GlobalExploreTab;
